import { random } from 'lodash';
import { useEffect, useState } from 'react';
import { Popover } from '@meitu/saas-ui';
import ToolTip from '@meitu/saas-ui/es/tooltip';
import type { Editor } from '@meitu/saas-editor-core';
import type { ILayerProps } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { useLocaleStore } from '@/i18n';

type AIPPTComponentProps = {
    editor: Editor;
    onClick: Function;
    activeIndex: number;
    index: number;
    // 从旧代码  const templateExtraInfo = useExtraInfoState(); 搬迁
    templateExtraInfo?: { [key: string]: any };
    // 从旧代码 import { fetchAIPPTComponentList } from '@/services/ppt'; 搬迁
    fetchAIPPTComponentList: (data: any) => Promise<any>;
    // 旧代码：import { getStagePoints} from '@/hooks/useFetchTemplate/applyPPTTemplate/replacePPTComponent';
    getStagePoints: (layers: ILayerProps[], page_type: string) => number;
    // 旧代码：import { replaceComponentKit } from '@/hooks/useFetchTemplate/applyPPTTemplate/replacePPTComponent';
    replaceComponentKit: (
        componentUrl: string,
        templateUrl: string,
        layers: ILayerProps[],
        fonts: any[],
        page_type: string,
        themeType: string,
    ) => Promise<any[]>;
};
const AIPPTComponent: React.FC<AIPPTComponentProps> = ({
    editor,
    onClick,
    activeIndex,
    index,
    templateExtraInfo,
    fetchAIPPTComponentList,
    getStagePoints,
    replaceComponentKit,
}) => {
    const [showTools, setShowTools] = useState(false);
    const [componentList, setComponentList] = useState<any[]>([]);
    const extraInfo = editor.stage.getExtraInfo(index);
    const [activeComponent, setActiveComponent] = useState<string>('');
    const [isReplacingComponent, setIsReplacingComponent] = useState(false);
    const [currentCursor, setCurrentCursor] = useState('');
    const fontList = editor.font.getList();
    const [id, setId] = useState(`id${random(0, 100000)}`);

    const { $t } = useLocaleStore();
    const [hasMore, setHasMoreComponent] = useState(false);
    useEffect(() => {
        if (!showTools) {
            return;
        }
        const done = (e: MouseEvent) => {
            const elem = document.getElementById('p-collapse');
            const wrap = document.getElementById(id);
            if (!wrap?.contains(e.target as any)) {
                setShowTools(false);
            }
        };
        document.addEventListener('mousedown', done);
        return () => {
            document.removeEventListener('mousedown', done);
        };
    }, [showTools]);

    useEffect(() => {
        if (activeIndex !== index) {
            setShowTools(false);
        }
    }, [activeIndex, index]);

    const getData = async (point: number, isFirst = false, cursor?: string) => {
        const pageType = extraInfo?.extraInfo?.label?.type;
        let layout = '';
        if (pageType === '目录') {
            layout = extraInfo?.extraInfo?.label?.layout || '';
        }
        const data = await fetchAIPPTComponentList({
            component_point: point,
            component_type: pageType === '内容' ? 2 : 1,
            cursor: cursor !== undefined ? cursor : currentCursor,
            layout: layout || undefined,
            exclude_material_ids: extraInfo?.extraInfo?.componentId
                ? [extraInfo.extraInfo.componentId].toString()
                : undefined,
        });
        const list = data.data.data.data;
        setComponentList(list);
        setCurrentCursor(data.data.data.cursor);

        if (data.data.data.cursor && isFirst) {
            setHasMoreComponent(true);
        }
    };

    useEffect(() => {
        if (!showTools) {
            return;
        }

        setCurrentCursor('');
        const pageType = extraInfo?.extraInfo?.label?.type;
        const layers = editor.stage.getStageByIndex(index);
        const point = getStagePoints(layers, pageType || '');
        if (!point) {
            return;
        }

        if (point <= 0 || point > 10) {
            return;
        }
        getData(point, true, '');
    }, [showTools]);

    useEffect(() => {
        return () => {
            console.log('组件被销毁了===');
        };
    }, []);

    if (
        !extraInfo ||
        !extraInfo?.extraInfo?.label ||
        (extraInfo?.extraInfo?.label?.type !== '目录' && extraInfo?.extraInfo?.label?.type !== '内容') ||
        extraInfo?.extraInfo?.isSmartComponent ||
        extraInfo?.extraInfo?.component_page
    ) {
        return null;
    }

    const replaceTemplate = async (templateInfo: any) => {
        if (
            templateInfo.id === activeComponent ||
            !templateExtraInfo ||
            !templateExtraInfo.originTemplate ||
            isReplacingComponent
        ) {
            return;
        }

        try {
            editor.tip.showMask({
                loading: true,
                text: $t((v) => v.collapse.navigationCollapse.componentReplacing),
                background: true,
            });
            setIsReplacingComponent(true);
            setActiveComponent(templateInfo.id);
            const layers = editor.stage.getStageByIndex(index);
            const resultLayers = await replaceComponentKit(
                templateInfo.json_url,
                templateExtraInfo.originTemplate,
                layers,
                fontList,
                extraInfo?.extraInfo?.label?.type || '',
                extraInfo?.extraInfo?.label?.theme || '',
            );

            const newExtrainfo = {
                ...extraInfo,
                extraInfo: {
                    ...extraInfo.extraInfo,
                    componentUrl: templateInfo.json_url,
                    componentId: templateInfo.id,
                },
            };
            editor.stage.replaceActive(resultLayers as ILayerProps[], newExtrainfo, false);

            setIsReplacingComponent(false);
            window._stat_track('tpbj_subfunction_tick', {
                func: '17_159',
                type: 2,
            });

            editor.tip.hideMask();
        } catch (error) {
            console.error('error===', error);
            editor.tip.hideMask();
            setIsReplacingComponent(false);
        }
    };

    const changeTemplate = (templateInfo: any) => {
        const pageType = extraInfo?.extraInfo?.label?.type;
        const layers = editor.stage.getStageByIndex(index);
        const point = getStagePoints(layers, pageType || '');
        getData(point);
    };

    return (
        <Popover
            placement="bottomLeft"
            content={
                <div
                    id={id}
                    className="c-navigation-collapse__item-aippt-ul"
                    style={{
                        height: `${hasMore ? '482px' : '446px'}`,
                    }}
                >
                    <div className="title">{$t((v) => v.collapse.navigationCollapse.replaceComponentTitle)}</div>
                    {componentList.map((item) => {
                        return (
                            <div
                                key={item.id}
                                className={`component-item ${activeComponent === item.id ? 'active' : ''}`}
                                onClick={() => replaceTemplate(item)}
                            >
                                <img src={item.thumbnail.url} alt="预览图" />
                            </div>
                        );
                    })}
                    {hasMore ? (
                        <div className="change" onClick={changeTemplate}>
                            <IconFont name="RefreshBold" fontSize={16} color="var(--content-btn-secondary)" />
                            <span
                                style={{
                                    color: 'var(--content-btn-secondary)',
                                }}
                            >
                                {$t((v) => v.collapse.navigationCollapse.changeAnotherComponent)}
                            </span>
                        </div>
                    ) : null}

                    {componentList.length === 0 ? (
                        <div className="empty">
                            <IconFont name="EmptyDrawerBold" fontSize={36} color="var(--content-system-quaternary)" />
                            <div style={{ marginTop: '12px' }}>
                                {$t((v) => v.collapse.navigationCollapse.noComponentTip)}
                            </div>
                        </div>
                    ) : null}
                </div>
            }
            overlayClassName="c-navigation-collapse__item-tools-popover"
            open={showTools}
        >
            <ToolTip placement="top" title="替换组件">
                <div
                    style={{
                        display: showTools ? 'flex' : 'none',
                    }}
                    onClick={(e) => {
                        setShowTools(!showTools);
                        if (!showTools) {
                            onClick();
                            window._stat_track('tpbj_subfunction_click', {
                                func: '17_159',
                                type: 2,
                            });
                        }
                    }}
                    className="c-navigation-collapse__item-tools left"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path
                            d="M9.34153 1.6834C9.16667 1.46967 8.85166 1.43817 8.63793 1.61304C8.42421 1.7879 8.39271 2.10291 8.56757 2.31663L9.94488 4.00002H1.00001C0.723863 4.00002 0.500006 4.22387 0.500006 4.50002C0.500006 4.77616 0.723863 5.00002 1.00001 5.00002H11C11.1932 5.00002 11.3691 4.88871 11.4518 4.71413C11.5346 4.53955 11.5093 4.33292 11.387 4.1834L9.34153 1.6834Z"
                            fill="white"
                        />
                        <path
                            d="M2.65848 10.3166C2.83335 10.5304 3.14836 10.5619 3.36208 10.387C3.5758 10.2121 3.6073 9.89712 3.43244 9.6834L2.05513 8.00002H11C11.2761 8.00002 11.5 7.77616 11.5 7.50002C11.5 7.22387 11.2761 7.00002 11 7.00002H1.00001C0.80681 7.00002 0.630902 7.11132 0.548171 7.2859C0.465439 7.46048 0.490688 7.66711 0.613027 7.81663L2.65848 10.3166Z"
                            fill="white"
                        />
                    </svg>
                </div>
            </ToolTip>
        </Popover>
    );
};

export default AIPPTComponent;
